import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

interface ProtectedRouteProps {
  children: ReactNode;
  requireTeacher?: boolean;
}

const ProtectedRoute = ({ children, requireTeacher = true }: ProtectedRouteProps) => {
  const location = useLocation();
  const navigate = useSafeNavigate();
  const { isAuthenticated, user, loadCurrentUser } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(!isAuthenticated);

  // On mount, attempt to load current user if not already authenticated
  useEffect(() => {
    // Skip if already authenticated
    if (isAuthenticated) {
      return;
    }

    let isMounted = true;
    const timeout = setTimeout(() => {
      if (isMounted) {
        console.warn('⚠️ Auth check timed out after 3 seconds');
        setIsInitializing(false);
      }
    }, 3000);

    // Attempt to load current user
    loadCurrentUser()
      .catch((error: unknown) => {
        console.error('❌ Failed to load current user:', error);
      })
      .finally(() => {
        if (isMounted) {
          setIsInitializing(false);
        }
        clearTimeout(timeout);
      });

    // Cleanup function
    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
    // Mount-only: re-running on isAuthenticated/loadCurrentUser changes would retrigger
    // the auth bootstrap loop that this effect exists to run exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show brief loading state during initialization
  if (isInitializing) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>
            🔐 Checking Authentication...
          </h2>
          <p style={{ margin: 0, opacity: 0.8 }}>
            Please wait while we verify your credentials
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Redirect to auth page if not authenticated
  if (!isAuthenticated || !user) {
    console.log('🔒 User not authenticated, redirecting to auth page');
    return (
      <Navigate
        to="/teacher-auth"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check if user has required role (teacher)
  if (requireTeacher && user.role !== 'teacher') {
    console.log('⚠️ User is not a teacher, access denied');
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          maxWidth: '500px',
          margin: '2rem'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
            🚫
          </div>
          <h2 style={{ margin: '0 0 1rem', fontSize: '2rem' }}>
            Access Denied
          </h2>
          <p style={{
            margin: '0 0 2rem',
            fontSize: '1.1rem',
            opacity: 0.9,
            lineHeight: '1.5'
          }}>
            This area is restricted to teachers only. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/lobby')}
            style={{
              padding: '1rem 2rem',
              fontSize: '1rem',
              fontWeight: '600',
              color: '#ef4444',
              background: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            ← Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has proper role, render children
  console.log('✅ User authenticated and authorized, rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute;
